import React, { useState } from 'react';
import './claim-entity.detail.notes.scss';
import './claim-history-log-details-modal.scss';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ClaimEntityPageContainer } from './claim-entity.page.container';
import {
  createTextColumn,
  CustomizedTable,
  NewConvergeButton,
  TableColumn,
  StackLayout,
} from '../../../presentation';

import { useClaimAuditQuery } from '@converge/graphql';
import { IClaimDetailsProps } from './claim-details';
import { useOutletContext } from 'react-router-dom';

export interface HistoryLogData {
  id: number;
  date: string | undefined | null;
  event: string | undefined | null;
  userName: string | undefined | null;
  description: string[] | undefined | null;
}
export const ClaimHistoryLogDetail = () => {
  const { claimUuid } = useOutletContext<IClaimDetailsProps>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDescriptions, setSelectedDescriptions] = useState<string[]>(
    []
  );
  const [isACESort, setIsACESort] = useState(true);

  const { loading, auditData } = useClaimAuditQuery(claimUuid);

  // Sort audit data on frontend based on isACESort state
  const sortedAuditData = auditData
    ? [...auditData].sort((a, b) => {
        const dateA = new Date(a.date || '').getTime();
        const dateB = new Date(b.date || '').getTime();

        // isACESort true = descending (newest first), false = ascending (oldest first)
        return isACESort ? dateB - dateA : dateA - dateB;
      })
    : [];

  // Format date from ISO to MM/DD/YYYY HH:MM:SS AM/PM using browser timezone
  const formatDateValue = (dateString: string): string => {
    if (!dateString) return dateString;

    try {
      const date = new Date(dateString);
      // Check if it's a valid date
      if (isNaN(date.getTime())) return dateString;

      // Adjust for timezone offset to get correct local time
      const timezoneOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
      const localDate = new Date(date.getTime() - timezoneOffset);

      // Get local date components to ensure proper local time display
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      let hours = localDate.getHours();
      const minutes = String(localDate.getMinutes()).padStart(2, '0');
      const seconds = String(localDate.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
    } catch {
      return dateString;
    }
  };

  const createDescriptionColumn = (): TableColumn<HistoryLogData> => ({
    id: 'description',
    label: 'DESCRIPTION',
    flex: '3.5',
    render: (
      value: string | number | string[] | null | undefined,
      row: HistoryLogData
    ) => {
      // Use value first, then fallback to row.description
      const rawDescriptions = value || row.description;
      // Ensure descriptions is always an array
      const descriptions = Array.isArray(rawDescriptions)
        ? rawDescriptions
        : [];

      if (descriptions.length === 0) {
        return '-';
      }

      if (descriptions.length === 1) {
        return descriptions[0];
      }

      return (
        <Box>
          {descriptions[0]},
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSelectedDescriptions(descriptions);
              setModalOpen(true);
            }}
            sx={{
              textTransform: 'none',
              p: 0,
              ml: 0.5,
              textDecoration: 'underline',
            }}
          >
            more details
          </Button>
        </Box>
      );
    },
  });

  const createDateColumn = (): TableColumn<HistoryLogData> => ({
    id: 'date',
    label: 'DATE',
    flex: '2',
    render: (
      value: string | number | string[] | null | undefined,
      row: HistoryLogData
    ) => {
      const dateValue = (value as string) || row.date;
      if (!dateValue) return '-';

      // Format with browser timezone - explicitly handle UTC to local conversion
      try {
        // Create date object from the timestamp
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return dateValue;

        // If the timestamp appears to be in UTC but being treated as local time,
        // we need to adjust for the timezone offset
        const timezoneOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds

        // Check if this looks like a UTC timestamp that's being displayed incorrectly
        // If the time seems way off (like 9:37 PM when it should be 1:37 PM),
        // it's likely a UTC timestamp being treated as local

        // Create a new date adjusted for timezone
        const localDate = new Date(date.getTime() - timezoneOffset);

        // Use the adjusted date for display
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        let hours = localDate.getHours();
        const minutes = String(localDate.getMinutes()).padStart(2, '0');
        const seconds = String(localDate.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
      } catch {
        return dateValue;
      }
    },
  });

  const historyLogColumns = [
    createDateColumn(),
    createTextColumn<HistoryLogData>('event', 'EVENT', { flex: '2' }),
    createTextColumn<HistoryLogData>('userName', 'USER', { flex: '2' }),
    createDescriptionColumn(),
  ];

  return (
    <>
      <Box>
        <ClaimEntityPageContainer
          title={'History Log'}
          titleChildren={
            <Box style={{ backgroundColor: 'white' }}>
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    margin: '0px 20px 0px 0px',
                    paddingBottom: '10px',
                    justifyContent: 'flex-end',
                    width: '80%',
                  }}
                >
                  <Box
                    className="sort-by-date"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsACESort((prev) => !prev)}
                  >
                    <Box style={{ marginRight: '8px' }}>SORT BY DATE</Box>
                    <Box>
                      {/* Arrow up means descending (newest first) */}
                      {isACESort ? (
                        <ArrowUpwardIcon />
                      ) : (
                        /* Arrow down means ascending (oldest first) */
                        <ArrowDownwardIcon />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box style={{ width: '80%', marginTop: '0px' }}>
                  <CustomizedTable<HistoryLogData>
                    title=""
                    columns={historyLogColumns}
                    data={sortedAuditData}
                    enableSorting={false}
                    enablePagination={true}
                    defaultRowsPerPage={10}
                    maxHeight="calc(100vh - 500px)"
                    loading={loading}
                  />
                </Box>
              </>
            </Box>
          }
        >
          <Box
            style={{ marginLeft: '10px', marginTop: '40px', width: '170px' }}
          ></Box>
        </ClaimEntityPageContainer>
      </Box>

      {/* Description Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '600px',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <StackLayout justifyContent="space-between">
            <Box sx={{ marginTop: '20px' }}>
              <Typography
                variant="h6"
                component="h2"
                className="modal-title-description"
              >
                Description Details
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setModalOpen(false)}
              size="small"
            >
              <CloseIcon
                sx={{
                  marginBottom: '0',
                  width: '24px',
                  height: '24px',
                  marginTop: '10px',
                }}
              />
            </IconButton>
          </StackLayout>
        </DialogTitle>
        <DialogContent sx={{ margin: '0px 30px' }}>
          <List className="description-list">
            {Array.isArray(selectedDescriptions) &&
              selectedDescriptions.map((description, index) => {
                // Check if description contains a colon to split key-value pairs
                const colonIndex = description.indexOf(':');
                if (colonIndex > 0) {
                  const key = description.substring(0, colonIndex + 1);
                  let value = description.substring(colonIndex + 1).trim();

                  // Check if key contains 'date' (case insensitive) and format the value
                  if (key.toLowerCase().includes('date')) {
                    value = formatDateValue(value);
                  }

                  return (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <Typography variant="body2">
                        <span className="description-label">{index + 1}.</span>
                        <span className="description-key">{key}</span>
                        {' ' + value}
                      </Typography>
                    </ListItem>
                  );
                } else {
                  return (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <Typography variant="body2">
                        <span className="description-label">{index + 1}.</span>{' '}
                        {description}
                      </Typography>
                    </ListItem>
                  );
                }
              })}
          </List>
        </DialogContent>
        <DialogActions className="button-container-description">
          <div className="close-btn-container">
            <NewConvergeButton
              onClick={() => setModalOpen(false)}
              variant="outlined"
              label="Close"
              size="small"
            />
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};
